import { setLocale } from "../../utils/localstorage";
import { useTranslation } from "react-i18next";
import { Select } from "@headlessui/react";
import clsx from "clsx";
import { ChevronDownIcon, Globe } from "lucide-react";

export default function LanguageSelector({
    variant = "default",
}: {
    variant?: "default" | "light" | "dark" | "minimal" | "green";
}) {
    const { i18n } = useTranslation();

    const handleLanguageChange = (value: string) => {
        i18n.changeLanguage(value);
        setLocale(value);
    };

    const getVariantStyles = () => {
        switch (variant) {
            case "light":
                return {
                    container:
                        "bg-gray-100 border-gray-200 hover:bg-gray-200 hover:shadow-md",
                    globe: "text-gray-600 group-hover:text-gray-800",
                    text: "text-gray-700",
                };
            case "dark":
                return {
                    container:
                        "bg-gray-800/80 border-gray-700 hover:bg-gray-700/80 hover:shadow-lg",
                    globe: "text-gray-300 group-hover:text-white",
                    text: "text-gray-200",
                };
            case "minimal":
                return {
                    container:
                        "bg-transparent border-gray-300 hover:bg-gray-50 hover:border-gray-400",
                    globe: "text-gray-500 group-hover:text-gray-700",
                    text: "text-gray-600",
                };
            case "green":
                return {
                    container:
                        "bg-green-500/20 border-green-400/30 hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/20",
                    globe: "text-green-700 group-hover:text-white",
                    text: "text-green-700 hover:text-white",
                };
            default: // Blue sidebar variant
                return {
                    container:
                        "bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 hover:shadow-lg",
                    globe: "text-blue-200 group-hover:text-white",
                    text: "text-blue-100",
                };
        }
    };

    const styles = getVariantStyles();

    return (
        <div className="group relative">
            <div
                className={clsx(
                    "flex items-center gap-2 px-2 py-1 rounded-lg transition-all duration-300 hover:scale-105",
                    styles.container,
                )}>
                <Globe
                    className={clsx("h-4 w-4 transition-colors", styles.globe)}
                />
                <Select
                    value={i18n.language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className={clsx(
                        "block w-full appearance-none bg-transparent border-none text-xs font-medium",
                        "focus:outline-none focus:ring-0 cursor-pointer",
                        "*:text-gray-800 *:bg-white *:font-medium",
                        styles.text,
                    )}>
                    <option value="en">🇺🇸 English</option>
                    <option value="fr">🇫🇷 Français</option>
                </Select>
            </div>
        </div>
    );
}
